import { useQuery } from '@apollo/client'

import { GetSwitchOrganisationModalSelectOrganisationDocument } from '@/graphql/purchase-plus/generated/getSwitchOrganisationModalSelectOrganisation.generated'
import {
  OrganisationsQuerySortsPropertyEnum,
  SortDirectionEnum,
} from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { PURCHASE_PLUS_GRAPHQL_API } from '@/modules/shared/constants'
import { useWatchQueryParams } from '@/modules/shared/hooks/useWatchQueryParams'
import { checkNetworkStatus } from '@/modules/shared/utils/checkNetworkStatus'
import { extractEdgesWithType } from '@/modules/shared/utils/extractEdgesWithType'
import { ignoreSingleCharacter } from '@/modules/shared/utils/ignoreSingleCharacter'
import { createPaginationUtils } from '@/modules/shared/utils/paginationUtils'

interface UseGetSwitchOrganisationModalSelectOrganisationProps {
  skip?: boolean
  searchText?: string
  idEq?: number
}

export const useGetSwitchOrganisationModalSelectOrganisation = ({
  skip,
  searchText: searchTextFromProps,
  idEq: idEqFromProps,
}: UseGetSwitchOrganisationModalSelectOrganisationProps) => {
  const paginationResultsPerPage = 20
  const { id, verified, legalEntity, businessNumber } = useWatchQueryParams()

  const { data, networkStatus, error, refetch, fetchMore } = useQuery(
    GetSwitchOrganisationModalSelectOrganisationDocument,
    {
      skip,
      variables: {
        after: null,
        first: paginationResultsPerPage,
        filters: {
          searchText: ignoreSingleCharacter(searchTextFromProps),
          idIn: [idEqFromProps || id].filter(Boolean) as number[],
          isVerified: verified,
          legalEntity,
          businessNumberEq: businessNumber,
        },
        sorts: [
          {
            direction: SortDirectionEnum.Asc,
            property: OrganisationsQuerySortsPropertyEnum.CreatedAt,
          },
        ],
        adminScope: true,
      },
      fetchPolicy: 'cache-and-network',
      context: { uri: PURCHASE_PLUS_GRAPHQL_API },
      notifyOnNetworkStatusChange: true,
    }
  )

  const pageInfo = data?.organisations?.pageInfo
  const loadingStates = checkNetworkStatus(networkStatus)

  return {
    error,
    organisations: extractEdgesWithType(data?.organisations),
    loadingStates,
    refetch,
    pageInfo,
    ...createPaginationUtils({
      pageInfo,
      refetch,
      fetchMore,
      paginationResultsPerPage,
    }),
  }
}
